import { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { api } from "../services/api";
import { Link } from "react-router-dom";

const formatTokens = (value) => Number(value || 0).toLocaleString("en-US");
const formatCost = (value) => `$${Number(value || 0).toFixed(4)}`;
const formatAdminDateTime = (value) => {
  const date = new Date(value);
  const pad = (num) => String(num).padStart(2, "0");
  return {
    date: `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear()).slice(-2)}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  };
};

const AdminSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(
    () => new URLSearchParams(window.location.search).get("q") || ""
  );
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const limit = 20;

  const fetchData = async (pageToLoad = 1) => {
    const query = new URLSearchParams({
      page: pageToLoad,
      limit,
      ...(search && { q: search }),
      sort_by: sortBy,
      sort_dir: sortDir,
    }).toString();

    const res = await api.get(`/api/admin/sessions/list?${query}`);
    setSessions(res.data.sessions);
    setPage(res.data.page);
    setTotal(res.data.total);
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const totalPages = Math.ceil(total / limit);

  const columns = [
    { label: "Date", field: "created_at" },
    { label: "User", field: "user" },
    { label: "Session Type", field: "pitch_type" },
    { label: "Duration", field: "duration_seconds" },
    { label: "Interactions", field: "interaction_count" },
    { label: "User Words", field: "user_word_count" },
    { label: "AI Words", field: "ai_word_count" },
    { label: "Score", field: "global_score" },
    { label: "Suggestion", field: "suggested_metric" },
    { label: "Resources", field: "resources", style: { minWidth: "180px" }, },
    { label: "Status", field: "status" },
  ];

  return (
    <Box sx={{
      backgroundImage: 'url("/assets/bg-pitchest.jpg")',
      backgroundSize: "cover",
      minHeight: "100vh",
      px: 2,
      py: 6,
    }}>
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">🎤 Sessions</Typography>
          <Link to="/admin" style={{ color: "#90caf9", fontWeight: "bold" }}>⬅ Back</Link>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
        Total: {total} — Page {page} of {totalPages}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchData(1);
            }}
            placeholder="Search name, email..."
            style={{
              padding: "6px 10px",
              backgroundColor: "#222",
              color: "#fff",
              border: "1px solid #7e57c2",
              borderRadius: "4px"
            }}
          />
          <Button
            variant="outlined"
            onClick={() => fetchData(1)}
            size="small"
            sx={{ ml: 1 }}
          >
            🔍 Search
          </Button>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => handleSort(col.field)}
                    style={{
                      padding: "8px",
                      backgroundColor: "#34163A",
                      cursor: "pointer",
                      ...(col.style || {}),
                    }}
                  >
                    {col.label} {sortBy === col.field && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, idx) => (
                <tr key={s.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                  <td style={{ padding: "8px" }}>
                    <div>{formatAdminDateTime(s.created_at).date}</div>
                    <div style={{ fontSize: "12px", color: "#ddd", marginTop: "4px" }}>
                      {formatAdminDateTime(s.created_at).time}
                    </div>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div>{s.user?.first_name} {s.user?.last_name}</div>
                    <div style={{ fontSize: "12px", color: "#ddd", marginTop: "4px" }}>
                      {s.user?.email}
                    </div>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div style={{ marginBottom: "4px" }}>
                      <span style={{ fontSize: "11px", color: "#d2b6de", marginRight: "6px" }}>PitchType:</span>
                      <span>{s.pitch_type}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span style={{ fontSize: "11px", color: "#d2b6de", marginRight: "6px" }}>AIProfile:</span>
                      <span>{s.ai_profile}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: "11px", color: "#d2b6de", marginRight: "6px" }}>Language:</span>
                      <span>{s.language}</span>
                    </div>
                  </td>
                  <td style={{ padding: "8px" }}>{Math.round(Number(s.duration_seconds || 0))}</td>
                  <td style={{ padding: "8px" }}>{s.interaction_count}</td>
                  <td style={{ padding: "8px" }}>{s.user_word_count}</td>
                  <td style={{ padding: "8px" }}>{s.ai_word_count}</td>
                  <td style={{ padding: "8px" }}>{s.global_score?.toFixed(2) || "-"}</td>
                  <td style={{ padding: "8px" }}>{s.suggested_metric || "-"}</td>
                  <td style={{ padding: "8px" }}>
                    <span style={{ fontSize: "13px", lineHeight: 1.35 }}>
                      {`Eval: ${s.models?.eval || "-"}`}
                      {s.evaluation_details?.model ? ` - ${s.evaluation_details.model}` : ""}
                      {s.evaluation_details?.reasoning_effort ? ` [${s.evaluation_details.reasoning_effort}]` : ""}
                    </span>
                    <span style={{ fontSize: "12px", color: "#aaa" }}>
                      {` (${formatTokens(s.usage?.eval_in)}/${formatTokens(s.usage?.eval_out)}tk ${formatCost(s.costs?.eval).replace("$", "")})`}
                    </span>
                    <br />
                    <span style={{ fontSize: "13px", lineHeight: 1.35 }}>
                      {`LLM: ${s.models?.llm || "-"}`}
                      {s.llm_details?.analyzer_model ? ` - ${s.llm_details.analyzer_model}` : ""}
                      {s.llm_details?.responder_model && s.llm_details?.responder_model !== s.llm_details?.analyzer_model
                        ? ` / ${s.llm_details.responder_model}`
                        : ""}
                    </span>
                    <span style={{ fontSize: "12px", color: "#aaa" }}>
                      {` (${formatTokens(s.usage?.llm_in)}/${formatTokens(s.usage?.llm_out)}tk ${formatCost(s.costs?.llm).replace("$", "")})`}
                    </span>
                    <br />
                    <span style={{ fontSize: "13px", lineHeight: 1.35 }}>
                      {`STT: ${s.models?.stt}`}
                    </span>
                    <span style={{ fontSize: "12px", color: "#aaa" }}>
                      {` (${s.usage?.stt}s ${formatCost(s.costs?.stt).replace("$", "")})`}
                    </span>
                    <br />
                    <span style={{ fontSize: "13px", lineHeight: 1.35 }}>
                      {`TTS: ${s.models?.tts}`}
                    </span>
                    <span style={{ fontSize: "12px", color: "#aaa" }}>
                      {` (${formatTokens(s.usage?.tts)}char ${formatCost(s.costs?.tts).replace("$", "")})`}
                    </span>
                    <br />
                    {`Full cost: ${formatCost(s.costs?.total)}`}
                  </td>
                  <td style={{ padding: "8px" }}>{s.status}</td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: "16px", textAlign: "center", backgroundColor: "#452654" }}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="outlined" disabled={page === 1} onClick={() => fetchData(page - 1)}>⬅ Prev</Button>
          <Button variant="outlined" disabled={page === totalPages || total === 0} onClick={() => fetchData(page + 1)}>Next ➡</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSessions;
